<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import Switch from 'v3/components/Form/Switch.vue';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle', 'edit', 'delete', 'clone']);

const createdOn = computed(() =>
  props.automation?.created_on
    ? messageStamp(new Date(props.automation.created_on), 'LLL d, yyyy')
    : ''
);
const createdOnTitle = computed(() =>
  props.automation?.created_on
    ? messageStamp(new Date(props.automation.created_on), 'LLL d, yyyy hh:mm a')
    : ''
);

function onToggle() {
  const { id, name, active } = props.automation || {};
  emit('toggle', { id, name, status: active });
}
function onEdit() {
  emit('edit', props.automation);
}
function onClone() {
  emit('clone', props.automation);
}
function onDelete() {
  emit('delete', props.automation);
}
</script>

<template>
  <div
    class="rounded-lg border border-n-gray-2 bg-n-gray-1 dark:border-n-vars-black-bd/80 dark:bg-n-vars-black-br/30 p-3 w-full text-n-gray-3"
  >
    <!-- Top: Name + actions -->
    <div class="flex items-start justify-between gap-2 mb-2">
      <div class="min-w-0">
        <h6 class="block font-semibold text-sm text-n-gray-7 dark:text-n-gray-3 truncate">
          {{ automation.name }}
        </h6>
        <p class="text-xs text-n-gray-5 truncate">
          {{ automation.description }}
        </p>
      </div>
      <div class="flex gap-1 flex-shrink-0">
        <woot-button
          v-tooltip.bottom="$t('AUTOMATION.FORM.EDIT')"
          variant="smooth"
          size="small"
          icon="edit"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          :is-loading="loading"
          @click="onEdit"
        />
        <woot-button
          v-tooltip.bottom="$t('AUTOMATION.CLONE.TOOLTIP')"
          variant="smooth"
          size="small"
          icon="copy"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          :is-loading="loading"
          @click="onClone"
        />
        <woot-button
          v-tooltip.bottom="$t('AUTOMATION.FORM.DELETE')"
          variant="smooth"
          size="small"
          icon="dismiss-circle"
          class-names="!bg-n-gray-25 !text-n-gray-5 !border-n-gray-1 dark:!border-n-vars-black-bd/80 dark:!bg-n-vars-black-bd/80 dark:!text-n-gray-3 !rounded-lg !w-8 !h-8 !p-0"
          :is-loading="loading"
          @click="onDelete"
        />
      </div>
    </div>

    <div class="flex items-center justify-between mb-2">
      <div class="text-xs text-n-gray-5">
        {{ $t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON') }}
      </div>
      <div class="text-xs font-medium text-n-gray-6 dark:text-n-gray-3" :title="createdOnTitle">
        {{ createdOn }}
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="text-sm font-medium text-n-gray-5">
        {{ $t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE') }}
      </div>
      <Switch :model-value="automation.active" @update:model-value="onToggle" />
    </div>
  </div>
</template>